<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Statutory Files (EPF / SOCSO / EIS / LHDN)</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-autocomplete
            v-model="payrollPeriod"
            v-validate="'required'"
            label="Payroll Period"
            item-text="payroll_date"
            item-value="id"
            :items="payrollPeriods"
            clearable
            hide-selected
            :error-messages="errors.collect('payrollPeriod')"
            data-vv-name="payrollPeriod"  
            data-vv-as="payroll period"
          />
        </v-col>

        <v-col>
          <v-autocomplete
            v-model="reportType"
            v-validate="'required'"
            label="Form/Report Types"   
            :items="reportList"
            :error-messages="errors.collect('reportType')"
            data-vv-name="reportType"  
            data-vv-as="report type"
            item-text="value"
            item-value="id"
          />
        </v-col>

        <v-col v-if="reportType === 'COMBINE_SOCSO_EIS'">
          <v-autocomplete
            v-model="selectedSchemes"
            v-validate="'required'"
            label="Schemes"
            :items="contributionSchemes"
            :error-messages="errors.collect('selectedSchemes')"
            data-vv-name="selectedSchemes"  
            data-vv-as="contribution schemes"
            item-text="value"
            item-value="value"
            multiple
            small-chips
            deletable-chips
            clearable
            hide-selected
          />
        </v-col>
       
        <v-col v-else />

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                :loading="progressing"
                color="primary"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row> 
    </v-card> 
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      payrollPeriods: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        reportType: null,
        reportList: [
          { id: 'EPF', value: 'EPF Form A' },
          { id: 'COMBINE_SOCSO_EIS', value: 'Combine SOCSO & EIS' },
          { id: 'EIS', value: 'EIS Import Text' },
          { id: 'SOCSO', value: 'SOCSO Import Text' },
          { id: 'CP39', value: 'MTD Text File (CP39)' },
          { id: 'EPF_RHB', value: 'EPF Import Excel (RHB)' },
          { id: 'SOCSO_RHB', value: 'SOCSO Import Excel (RHB)' },
          { id: 'EIS_RHB', value: 'EIS Import Excel (RHB)' },
          { id: 'EPF_AMBANK', value: 'EPF Import Excel (AMBANK)' },
          { id: 'SOCSO_AMBANK', value: 'SOCSO Import Excel (AMBANK)' },
          { id: 'EIS_AMBANK', value: 'EIS Import Excel (AMBANK)' },
          { id: 'LHDN_AMBANK', value: 'LHDN Import Excel (AMBANK)' },
        ],
        contributionSchemes: [
          'SOCSO & SKBBK', 'EIS',
        ],
        progressing: false,
        progressbar: 0,
        payrollPeriod: null,
        selectedSchemes: null,
      }
    },
    methods: {
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            if (this.reportType) {
              if (this.reportType === 'EPF') {
                this.generateEpfFormA()
              } else if (this.reportType === 'EIS') {
                this.generateEisImportText()
              } else if (this.reportType === 'SOCSO') {
                this.generateSocosImportText()
              } else if (this.reportType === 'CP39') {
                this.generateCp39ImportText()
              } else if (['EPF_RHB', 'SOCSO_RHB', 'EIS_RHB', 'EPF_AMBANK', 'SOCSO_AMBANK', 'EIS_AMBANK', 'LHDN_AMBANK'].includes(this.reportType)) {
                this.generateBankStatutoryImportExcel(this.reportType)
              } else if (this.reportType === 'COMBINE_SOCSO_EIS') {
                this.generateCombineSocosEisImportText()
              }
            }
          })
      },

      generateEpfFormA () {
        this.$api.payrollCycleTransactions().epfFormA(this.payrollPeriod, {
          onDownloadProgress: (progressEvent) => {
            this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
          },
        })
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/zip' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', 'EpfFormA.zip')  
            link.click()

            this.progressing = false
            this.progressbar = 0

            this.$snack({ message: 'Report downloaded successfully' })
          })
      },
      generateEisImportText () {
        this.$api.payrollCycleTransactions().eisImportText(this.payrollPeriod, {
          onDownloadProgress: (progressEvent) => {
            this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
          },
        })
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/zip' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', 'EisImport.zip') 
            link.click()

            this.progressing = false
            this.progressbar = 0

            this.$snack({ message: 'Report downloaded successfully' })
          })
      },
      generateSocosImportText () {
        this.$api.payrollCycleTransactions().socsoImportText(this.payrollPeriod, {
          onDownloadProgress: (progressEvent) => {
            this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
          },
        })
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/zip' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', 'SocsoImport.zip') 
            link.click()

            this.progressing = false
            this.progressbar = 0

            this.$snack({ message: 'Report downloaded successfully' })
          })
      },
      generateCombineSocosEisImportText () {
        this.$api.payrollCycleTransactions().socsoEisImportText(this.payrollPeriod, this.selectedSchemes, {
          onDownloadProgress: (progressEvent) => {
            this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
          },
        })
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/zip' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', 'SocsoEisImport.zip') 
            link.click()

            this.progressing = false
            this.progressbar = 0

            this.$snack({ message: 'Report downloaded successfully' })
          })
      },
      generateCp39ImportText () {
        this.$api.payrollCycleTransactions().cp39ImportText(this.payrollPeriod, {
          onDownloadProgress: (progressEvent) => {
            this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
          },
        })
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/zip' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', 'CP39.zip') 
            link.click()

            this.progressing = false
            this.progressbar = 0

            this.$snack({ message: 'Report downloaded successfully' })
          })
          .catch(error => {
            this.progressing = false
            if (error.response.status === 400) {
              this.$snack({ color: 'error', message: 'Data not found' })
            }
          })
      },
      generateBankStatutoryImportExcel (reportType) {
        // e.g. 'EPF_RHB' => report_type: 'EPF', bank: 'RHB'
        const [type, bank] = reportType.split('_')

        this.$api.payrollCycleTransactions().bankStatutoryImportExcel(this.payrollPeriod, { 'report_type': type, 'bank': bank }, {
          onDownloadProgress: (progressEvent) => {
            this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
          },
        })
          .then(res => {
            let blob = new Blob([res.data], { type: 'application/zip' })
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.setAttribute('download', reportType + '.zip')
            link.click()

            this.progressing = false
            this.progressbar = 0

            this.$snack({ message: 'Report downloaded successfully' })
          })
          .catch(error => {
            this.progressing = false

            const defaultMessage = 'Error! Please contact support'
            const data = (error.response || {}).data

            if (data instanceof Blob && error.response.status === 400) {
              data.text().then(text => {
                let message = defaultMessage

                try {
                  message = JSON.parse(text).message || defaultMessage
                } catch (e) {
                  message = defaultMessage
                }

                this.$snack({ color: 'error', message })
              })
            } else {
              this.$snack({ color: 'error', message: defaultMessage })
            }
          })
      },
    },
  }
</script>
